export interface IRequestedAmount {
  currencyDescription?: string;
  currencyIsoCode?: string;
  currencySymbol?: string;
  currencyId?: string;
  commodity?: string;
  commodityCode?: string;
  commodityDescription?: string;
  totalAmount?: number;
  denominations?: ISolCustomerDenominationQuantity[];
  packs?: ISolCustomerPackQuantity[];
}

export enum SolCustomerOrderProductCode {
  PICKUP_CASH = "1",
  PICKUP_OTHERS = "2",
  DELIVERY_CASH = "11",
  DELIVERY_OTHERS = "12",
  DELIVERY_EXCHANGE = "55",
  ASSISTANCE = "40",
  SUPPLY_MATERIAL = "14",
}

export enum SolCustomerOrderServiceType {
  PICKUP = 0,
  DELIVERY = 1,
  ASSISTANCE = 3,
  SUPPLY_MATERIAL = 4,
}

export enum SolCustomerOrderType {
  PICKUP = "PICKUP",
  PICKUP_OTHERS = "PICKUP_OTHERS",
  DELIVERY = "DELIVERY",
  DELIVERY_EXCHANGE = "DELIVERY_EXCHANGE",
  ASSISTANCE = "ASSISTANCE",
  SUPPLY_MATERIAL = "SUPPLY_MATERIAL",
}

export enum SolCustomerWoStatusCode {
  PENDING = 0,
  TO_PLAN = 1,
  PLANNED = 2,
  TO_CONFIRM = 3,
  ON_ROUTE = 4,
  IN_DISPLACEMENT = 5,
  IN_EXECUTION = 6,
  CONCLUDED = 7,
  FINISHED = 8,
  CLOSED = 9,
  TO_CANCEL = 10,
  CANCELLED = 11,
}

const ORDER_TYPE_BY_PRODUCT_CODE: Record<string, SolCustomerOrderType> = {
  [SolCustomerOrderProductCode.PICKUP_CASH]: SolCustomerOrderType.PICKUP,
  [SolCustomerOrderProductCode.PICKUP_OTHERS]: SolCustomerOrderType.PICKUP_OTHERS,
  [SolCustomerOrderProductCode.DELIVERY_CASH]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderProductCode.DELIVERY_OTHERS]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderProductCode.DELIVERY_EXCHANGE]: SolCustomerOrderType.DELIVERY_EXCHANGE,
  [SolCustomerOrderProductCode.ASSISTANCE]: SolCustomerOrderType.ASSISTANCE,
  [SolCustomerOrderProductCode.SUPPLY_MATERIAL]: SolCustomerOrderType.SUPPLY_MATERIAL,
};

const ORDER_TYPE_BY_SERVICE_TYPE: Partial<
  Record<SolCustomerOrderServiceType, SolCustomerOrderType>
> = {
  [SolCustomerOrderServiceType.PICKUP]: SolCustomerOrderType.PICKUP,
  [SolCustomerOrderServiceType.DELIVERY]: SolCustomerOrderType.DELIVERY,
  [SolCustomerOrderServiceType.ASSISTANCE]: SolCustomerOrderType.ASSISTANCE,
  [SolCustomerOrderServiceType.SUPPLY_MATERIAL]: SolCustomerOrderType.SUPPLY_MATERIAL,
};

const ORDER_DESCRIPTION_KEY_BY_TYPE: Record<SolCustomerOrderType, string> = {
  [SolCustomerOrderType.PICKUP_OTHERS]: "label.product.pickup-others",
  [SolCustomerOrderType.PICKUP]: "label.product.pickup-cash",
  [SolCustomerOrderType.DELIVERY]: "label.product.delivery-cash",
  [SolCustomerOrderType.DELIVERY_EXCHANGE]: "label.product.delivery-exchange",
  [SolCustomerOrderType.ASSISTANCE]: "label.product.assistance-service",
  [SolCustomerOrderType.SUPPLY_MATERIAL]: "label.product.supply-material",
};

export const resolveSolCustomerOrderType = (
  productCode?: string,
  serviceType?: number
): SolCustomerOrderType | undefined => {
  if (productCode && ORDER_TYPE_BY_PRODUCT_CODE[productCode]) {
    return ORDER_TYPE_BY_PRODUCT_CODE[productCode];
  }

  if (typeof serviceType === "number") {
    return ORDER_TYPE_BY_SERVICE_TYPE[serviceType as SolCustomerOrderServiceType];
  }

  return undefined;
};

export const resolveSolCustomerOrderDescriptionKey = (
  orderType?: SolCustomerOrderType,
  isChange?: boolean
): string | undefined => {
  if (!orderType) {
    return undefined;
  }

  if (orderType === SolCustomerOrderType.DELIVERY && isChange) {
    return "label.product.delivery-exchange";
  }

  return ORDER_DESCRIPTION_KEY_BY_TYPE[orderType];
};

export interface IOrderTypeOption {
  type: SolCustomerOrderType;
  labelKey: string;
}

export const getLimitOrderTypeOptions = (
  labelOverrides?: Partial<Record<SolCustomerOrderType, string>>
): IOrderTypeOption[] => {
  const typesToInclude = [SolCustomerOrderType.PICKUP, SolCustomerOrderType.DELIVERY];
  return typesToInclude.map((type) => ({
    type,
    labelKey: labelOverrides?.[type] ?? ORDER_DESCRIPTION_KEY_BY_TYPE[type],
  }));
};

export interface IRequestHistorialsService {
  isChange?: boolean;
  origin?: string;
  requestFinalTime?: string;
  requestInitialTime?: string;
  plannedFinalTime?: string;
  plannedInitialTime?: string;
  realFinalTime?: string;
  realInitialTime?: string;
  estimatedArrivalDatetime?: string;
  createdAt?: string;
  approvalDate?: string;
  approvedAt?: string;
  comments?: string;
  requestedAmounts?: IRequestedAmount[];
  requestedTime?: string;
  serviceStatusCode?: string;
  serviceStatusDescription?: string;
  serviceType?: number;
  productCode?: string;
  productDescription?: string;
  serviceId?: string;
  serviceCode?: string;
  serviceRate?: number;
  serviceRateId?: string;
  consignmentStatusCode?: string;
  woStatusCode?: number;
  woStatusDescription?: string;
  pendingSend?: boolean;
}

export interface IRequestHistorials {
  services?: IRequestHistorialsService[];
}

export interface ISolCustomerOrderServiceContainer {}

export interface IPickUpService extends ISolCustomerOrderService {
  isChange?: boolean;
  productCode?: string;
  requestedAmounts?: IRequestedAmount[];
  serviceAmount?: string;
  agreementCommodities?: ISolCustomerCommodity[];
}

export interface IDeliveryService extends ISolCustomerOrderService {
  isChange?: boolean;
  requestedAmounts?: IRequestedAmount[];
}

export interface IFault {
  faultCode: string;
  faultDescription?: string;
  faultID: string;
}

export interface IAssistanceService extends ISolCustomerOrderService {
  assistanceObservation?: string;
  faultDescription?: string;
}

export interface ISolCustomerDenominationPack {
  code?: string;
  currencyIsoCode?: string;
  currencySymbol?: string;
  description?: string;
  id?: string;
  quantity?: number;
  value?: number;
}

export interface ISolCustomerPack {
  code?: string;
  denominations?: ISolCustomerDenominationPack[];
  description?: string;
  id?: string;
}

export interface ISolCustomerDenominationQuantity {
  denominationQuantity?: number | null;
  denominationId: string;
}

export interface ISolCustomerPackQuantity {
  packQuantity?: number | null;
  packId: string;
}

export interface ISolCustomerCommodity {
  code?: string;
  description?: string;
  id?: string;
}

export interface ISolCustomerCommodityType {
  code?: string;
  commodities?: ISolCustomerCommodity[];
  description?: string;
  id?: string;
}

export interface ISolCustomerSupply {
  commodityTypeId?: string;
  commodityTypeDescription?: string;
  commodityId?: string;
  commodityDescription?: string;
  quantity?: number;
}

export interface ISolCustomerSupplyMaterialService extends ISolCustomerOrderService {
  supplies?: ISolCustomerSupply[];
}

export interface ISolCustomerServiceRating {
  countryCode?: string;
  serviceId: string;
  rate: number;
  comments: string;
  optionsIds?: any[];
}

export interface ISolCustomerOrderContainer {
  containerSeal?: string;
  containerCode?: string;
}

export interface ISolCustomerOrderAmounts {
  commodityCode?: string;
  commodityDescription?: string;
  currencyIsoCode?: string;
  currencyDescription?: string;
  totalAmount?: string;
  quantity?: string;
}

export interface ISolCustomerOrderDocument {
  documentNumber?: string;
  container?: ISolCustomerOrderContainer[];
  amounts?: ISolCustomerOrderAmounts[];
}

export interface ISolCustomerOrderService {
  servicePointId?: string;
  userName?: string;
  countryCode?: string;
  origin?: string;
  hasDate?: boolean;
  hasLimitDate?: boolean;
  comments?: string;
  description?: string;
  motiveId?: string;
  requestFinalTime?: string;
  requestInitialTime?: string;
  responsable?: string;
  serviceDate?: string;
  serviceId?: string;
  address?: string;
  applicant?: string;
  centerDescription?: string;
  container?: ISolCustomerOrderServiceContainer[];
  custodianName?: string;
  documents?: ISolCustomerOrderDocument[];
  entityDescription?: string;
  plannedFinalTime?: string;
  plannedInitialTime?: string;
  realFinalTime?: string;
  realInitialTime?: string;
  routeCode?: string;
  serviceCode?: string;
  serviceLimitDate?: string;
  servicePointDescription?: string;
  motiveCode?: string;
  motiveDescription?: string;
  observation?: string;
}

export interface ISolCustomerCancellationReason {
  code?: string;
  description?: string;
}

export interface ISolCommodityCounter {
  commodityId?: string;
  maxQuantity?: number;
  counter?: number;
}

// CASH ORDER SEND STATUS

export enum CashOrderSendStatusEnum {
  PENDING_SENT = "PENDING_SENT",
  SENT = "SENT",
  CANCELED = "CANCELED",
  FAILED = "FAILED",
}

export interface ICashOrderSendStatus {
  sendStatus: CashOrderSendStatusEnum;
  label: string;
}

export enum SourceRequestViewEnum {
  APPROVALS = "APPROVALS",
  MONITORS = "MONITORS",
}

export interface IAmountByCurrency {
  amount: number;
  currencyIsoCode: string;
  currencyId?: string;
  currencySymbol: string;
}
